import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Mail,
  Send,
  Plus,
  RefreshCw,
  Eye,
  Edit3,
  Trash2,
  XCircle,
  CheckCircle,
  Clock,
  BarChart3,
  Megaphone,
  Newspaper,
  CalendarDays,
  Rocket,
  ArrowUpCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Users,
  Filter,
  Search,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_HEADERS = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const TEMPLATE_META = {
  announcement: { label: 'Announcement', Icon: Megaphone, color: 'bg-blue-500', gradient: 'from-blue-500 to-blue-700', pill: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', desc: 'Bold blue header — ideal for company-wide announcements and important news.' },
  update: { label: 'Update', Icon: ArrowUpCircle, color: 'bg-green-500', gradient: 'from-green-500 to-green-700', pill: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', desc: 'Fresh green header — great for product updates, improvements, and progress reports.' },
  newsletter: { label: 'Newsletter', Icon: Newspaper, color: 'bg-purple-500', gradient: 'from-purple-500 to-purple-700', pill: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', desc: 'Elegant purple header — perfect for weekly digests, newsletters, and roundups.' },
  event: { label: 'Event', Icon: CalendarDays, color: 'bg-orange-500', gradient: 'from-orange-500 to-orange-700', pill: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', desc: 'Vibrant orange header — suited for event invitations, webinars, and meetups.' },
  feature_release: { label: 'Feature Release', Icon: Rocket, color: 'bg-indigo-500', gradient: 'from-indigo-500 to-indigo-700', pill: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300', desc: 'Deep indigo header — designed for feature launches and release notes.' },
};

const STATUS_META = {
  draft: { label: 'Draft', pill: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-300' },
  scheduled: { label: 'Scheduled', pill: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  sending: { label: 'Sending', pill: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  sent: { label: 'Sent', pill: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  failed: { label: 'Failed', pill: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  cancelled: { label: 'Cancelled', pill: 'bg-secondary-100 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-400' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const StatCard = ({ icon: Icon, label, value, tone = 'primary' }) => {
  const tones = {
    primary: 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    orange: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  };
  return (
    <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-4 flex items-center gap-4">
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold text-secondary-900 dark:text-white leading-tight">{value}</div>
        <div className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">{label}</div>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.pill}`}>
      {meta.label}
    </span>
  );
};

const TemplatePill = ({ template }) => {
  const meta = TEMPLATE_META[template] || TEMPLATE_META.announcement;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.pill}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

// ─── Compose Tab ────────────────────────────────────────────────────────────

const ComposeTab = ({ onCampaignCreated }) => {
  const [form, setForm] = useState({
    name: '', template: 'announcement', subject: '', heading: '', body: '',
    cta_text: '', cta_url: '',
  });
  const [recipientFilter, setRecipientFilter] = useState({ roles: [], departments: [], teams: [], is_active: true });
  const [filterOptions, setFilterOptions] = useState({ roles: [], departments: [], teams: [] });
  const [recipientList, setRecipientList] = useState([]);
  const [excludedEmails, setExcludedEmails] = useState(new Set());
  const [loadingCount, setLoadingCount] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');

  const activeRecipientCount = recipientList.length - excludedEmails.size;

  useEffect(() => {
    axios.get('/api/promotional-emails/filters/options', { headers: API_HEADERS() })
      .then(r => setFilterOptions(r.data))
      .catch(() => {});
  }, []);

  const fetchRecipients = useCallback(async () => {
    setLoadingCount(true);
    try {
      const res = await axios.post('/api/promotional-emails/recipients/count', recipientFilter, { headers: API_HEADERS() });
      setRecipientList(res.data.recipients || []);
      setExcludedEmails(prev => {
        const validEmails = new Set((res.data.recipients || []).map(r => r.email));
        const cleaned = new Set([...prev].filter(e => validEmails.has(e)));
        return cleaned;
      });
    } catch { setRecipientList([]); }
    finally { setLoadingCount(false); }
  }, [recipientFilter]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const handleSubmit = async (sendNow) => {
    if (!form.name || !form.subject || !form.heading || !form.body) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!sendNow && !scheduledAt) {
      toast.error('Please select a scheduled date/time');
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        recipient_filter: recipientFilter,
        excluded_emails: excludedEmails.size > 0 ? [...excludedEmails] : null,
        send_now: sendNow,
        scheduled_at: sendNow ? null : new Date(scheduledAt).toISOString(),
      };
      await axios.post('/api/promotional-emails/', payload, { headers: API_HEADERS() });
      toast.success(sendNow ? 'Campaign sending started!' : 'Campaign scheduled successfully!');
      setForm({ name: '', template: 'announcement', subject: '', heading: '', body: '', cta_text: '', cta_url: '' });
      setScheduledAt('');
      setExcludedEmails(new Set());
      onCampaignCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    } finally { setSending(false); }
  };

  const handlePreview = async () => {
    try {
      const res = await axios.post('/api/promotional-emails/preview', {
        template: form.template, subject: form.subject, heading: form.heading || 'Preview Heading',
        body: form.body || 'Preview body content', cta_text: form.cta_text, cta_url: form.cta_url,
      }, { headers: API_HEADERS() });
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch { toast.error('Failed to generate preview'); }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleFilterArray = (field, value) => {
    setRecipientFilter(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  return (
    <div className="space-y-6">
      {/* Template Selector */}
      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-4">Choose Template</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(TEMPLATE_META).map(([key, meta]) => {
            const { Icon } = meta;
            const selected = form.template === key;
            return (
              <button
                key={key}
                onClick={() => updateField('template', key)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                  selected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
                    : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300 dark:hover:border-secondary-600'
                }`}
              >
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center text-white ${meta.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className={`text-xs font-medium ${selected ? 'text-primary-700 dark:text-primary-300' : 'text-secondary-600 dark:text-secondary-400'}`}>
                  {meta.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Template preview strip */}
        {form.template && TEMPLATE_META[form.template] && (() => {
          const tpl = TEMPLATE_META[form.template];
          const TplIcon = tpl.Icon;
          return (
            <div className={`mt-4 rounded-xl overflow-hidden border border-secondary-200 dark:border-secondary-700`}>
              <div className={`bg-gradient-to-r ${tpl.gradient} px-5 py-3 flex items-center gap-3`}>
                <TplIcon className="h-5 w-5 text-white/90" />
                <span className="text-sm font-semibold text-white">{form.heading || form.subject || tpl.label}</span>
              </div>
              <div className="px-5 py-3 bg-secondary-50 dark:bg-secondary-900">
                <p className="text-xs text-secondary-600 dark:text-secondary-400 leading-relaxed">{tpl.desc}</p>
                {(form.heading || form.body) && (
                  <div className="mt-2 pt-2 border-t border-secondary-200 dark:border-secondary-700">
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 italic">
                      {form.body
                        ? (form.body.length > 120 ? form.body.slice(0, 120) + '...' : form.body)
                        : 'Your email body will appear here...'}
                    </p>
                    {form.cta_text && (
                      <span className={`inline-block mt-2 px-3 py-1 rounded text-[10px] font-semibold text-white bg-gradient-to-r ${tpl.gradient}`}>
                        {form.cta_text}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Email Content */}
      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-4">Email Content</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Campaign Name *</label>
              <input type="text" value={form.name} onChange={e => updateField('name', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                placeholder="e.g. April Product Update" />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Subject Line *</label>
              <input type="text" value={form.subject} onChange={e => updateField('subject', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                placeholder="Email subject line" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Heading *</label>
            <input type="text" value={form.heading} onChange={e => updateField('heading', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              placeholder="Main heading in the email body" />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Body *</label>
            <textarea rows={6} value={form.body} onChange={e => updateField('body', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none"
              placeholder="Write your email content here. Use new lines to separate paragraphs." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">CTA Button Text</label>
              <input type="text" value={form.cta_text} onChange={e => updateField('cta_text', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                placeholder="e.g. Learn More" />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">CTA URL</label>
              <input type="url" value={form.cta_url} onChange={e => updateField('cta_url', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                placeholder="https://..." />
            </div>
          </div>
        </div>
      </div>

      {/* Recipient Filters */}
      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Recipients</h3>
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-secondary-400" />
            {loadingCount ? (
              <Loader2 className="h-4 w-4 animate-spin text-secondary-400" />
            ) : (
              <span className="font-semibold text-primary-600 dark:text-primary-400">{activeRecipientCount}</span>
            )}
            <span className="text-secondary-500 dark:text-secondary-400">
              recipients{excludedEmails.size > 0 && ` (${excludedEmails.size} excluded)`}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Roles */}
          {filterOptions.roles.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-2">Roles</label>
              <div className="flex flex-wrap gap-2">
                {filterOptions.roles.map(role => (
                  <button key={role} onClick={() => toggleFilterArray('roles', role)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      recipientFilter.roles.includes(role)
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-400'
                        : 'border-secondary-200 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:border-secondary-300'
                    }`}>
                    {role}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Departments */}
          {filterOptions.departments.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-2">Departments</label>
              <div className="flex flex-wrap gap-2">
                {filterOptions.departments.map(dep => (
                  <button key={dep} onClick={() => toggleFilterArray('departments', dep)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      recipientFilter.departments.includes(dep)
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-400'
                        : 'border-secondary-200 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:border-secondary-300'
                    }`}>
                    {dep}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Teams */}
          {filterOptions.teams.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-2">Teams</label>
              <div className="flex flex-wrap gap-2">
                {filterOptions.teams.map(team => (
                  <button key={team} onClick={() => toggleFilterArray('teams', team)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      recipientFilter.teams.includes(team)
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-400'
                        : 'border-secondary-200 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:border-secondary-300'
                    }`}>
                    {team}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Active toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={recipientFilter.is_active}
              onChange={e => setRecipientFilter(prev => ({ ...prev, is_active: e.target.checked }))}
              className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500" />
            <span className="text-xs text-secondary-600 dark:text-secondary-400">Verified / active users only</span>
          </label>
        </div>

        {/* Recipient List */}
        {recipientList.length > 0 && (
          <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-secondary-600 dark:text-secondary-400">
                {recipientList.length} users matched — uncheck to exclude
              </span>
              <div className="flex gap-2">
                <button onClick={() => setExcludedEmails(new Set())}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline">Select All</button>
                <button onClick={() => setExcludedEmails(new Set(recipientList.map(r => r.email)))}
                  className="text-xs text-secondary-500 dark:text-secondary-400 hover:underline">Deselect All</button>
              </div>
            </div>
            {/* Search within recipients */}
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-secondary-400" />
              <input type="text" value={recipientSearch} onChange={e => setRecipientSearch(e.target.value)}
                placeholder="Search recipients..."
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-xs focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none" />
            </div>
            <div className="max-h-56 overflow-y-auto space-y-0.5 pr-1">
              {recipientList
                .filter(r => {
                  if (!recipientSearch) return true;
                  const q = recipientSearch.toLowerCase();
                  return r.email.toLowerCase().includes(q) || r.name.toLowerCase().includes(q) || (r.department || '').toLowerCase().includes(q);
                })
                .map(r => {
                  const isExcluded = excludedEmails.has(r.email);
                  return (
                    <label key={r.email}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        isExcluded
                          ? 'opacity-50 bg-secondary-50 dark:bg-secondary-900/50'
                          : 'hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                      }`}>
                      <input type="checkbox"
                        checked={!isExcluded}
                        onChange={() => {
                          setExcludedEmails(prev => {
                            const next = new Set(prev);
                            if (next.has(r.email)) next.delete(r.email);
                            else next.add(r.email);
                            return next;
                          });
                        }}
                        className="h-3.5 w-3.5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-secondary-900 dark:text-white truncate">{r.name}</span>
                          {r.role && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 dark:bg-secondary-700 text-secondary-500 dark:text-secondary-400 flex-shrink-0">
                              {r.role}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-secondary-500 dark:text-secondary-400 truncate">{r.email}{r.department ? ` · ${r.department}` : ''}</div>
                      </div>
                    </label>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
          <div className="flex-1 w-full sm:w-auto">
            <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Schedule Send</label>
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
              className="w-full px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none" />
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={handlePreview}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 text-sm font-medium hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors">
              <Eye className="h-4 w-4" /> Preview
            </button>
            <button onClick={() => handleSubmit(false)} disabled={sending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium transition-colors disabled:opacity-50">
              <Clock className="h-4 w-4" /> Schedule
            </button>
            <button onClick={() => handleSubmit(true)} disabled={sending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Now
            </button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
              <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Email Preview</h3>
              <button onClick={() => setShowPreview(false)} className="p-1 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors">
                <X className="h-4 w-4 text-secondary-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[75vh]">
              <iframe title="Email Preview" srcDoc={previewHtml} className="w-full h-[60vh] border border-secondary-200 dark:border-secondary-700 rounded-lg" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Campaigns Tab ──────────────────────────────────────────────────────────

const CampaignsTab = ({ refreshKey }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const limit = 10;

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`/api/promotional-emails/?${params}`, { headers: API_HEADERS() });
      setCampaigns(res.data.campaigns || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns, refreshKey]);

  const totalPages = Math.ceil(total / limit);

  const handleAction = async (campaignId, action) => {
    try {
      if (action === 'send') {
        await axios.post(`/api/promotional-emails/${campaignId}/send`, {}, { headers: API_HEADERS() });
        toast.success('Campaign sending started');
      } else if (action === 'cancel') {
        await axios.post(`/api/promotional-emails/${campaignId}/cancel`, {}, { headers: API_HEADERS() });
        toast.success('Campaign cancelled');
      } else if (action === 'delete') {
        await axios.delete(`/api/promotional-emails/${campaignId}`, { headers: API_HEADERS() });
        toast.success('Campaign deleted');
      }
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} campaign`);
    }
  };

  const statuses = ['', 'draft', 'scheduled', 'sending', 'sent', 'failed', 'cancelled'];

  return (
    <div className="space-y-4">
      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {statuses.map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              statusFilter === s
                ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-400'
                : 'border-secondary-200 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:border-secondary-300'
            }`}>
            {s ? (STATUS_META[s]?.label || s) : 'All'}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-16">
            <Mail className="mx-auto h-10 w-10 text-secondary-300 dark:text-secondary-600" />
            <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">No campaigns found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-secondary-200 dark:border-secondary-700">
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Campaign</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Template</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Recipients</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Delivery</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                {campaigns.map(c => (
                  <tr key={c.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedCampaign(c)}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-secondary-900 dark:text-white">{c.name}</div>
                      <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate max-w-[200px]">{c.subject}</div>
                    </td>
                    <td className="px-4 py-3"><TemplatePill template={c.template} /></td>
                    <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                    <td className="px-4 py-3 text-sm text-secondary-700 dark:text-secondary-300">{c.total_recipients}</td>
                    <td className="px-4 py-3">
                      {c.status === 'sent' || c.status === 'failed' ? (
                        <div className="text-xs">
                          <span className="text-green-600 dark:text-green-400">{c.successful}</span>
                          <span className="text-secondary-400 mx-1">/</span>
                          <span className="text-red-500 dark:text-red-400">{c.failed}</span>
                        </div>
                      ) : <span className="text-xs text-secondary-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-secondary-500 dark:text-secondary-400">
                      {c.sent_at ? formatDate(c.sent_at) : c.scheduled_at ? formatDate(c.scheduled_at) : formatDate(c.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {(c.status === 'draft' || c.status === 'failed') && (
                          <button onClick={() => handleAction(c.id, 'send')} title="Send now"
                            className="p-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400 transition-colors">
                            <Send className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {c.status === 'scheduled' && (
                          <button onClick={() => handleAction(c.id, 'cancel')} title="Cancel"
                            className="p-1.5 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 transition-colors">
                            <XCircle className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {(c.status === 'draft' || c.status === 'scheduled') && (
                          <button onClick={() => handleAction(c.id, 'delete')} title="Delete"
                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-200 dark:border-secondary-700">
            <span className="text-xs text-secondary-500 dark:text-secondary-400">
              Page {page} of {totalPages} ({total} campaigns)
            </span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded-lg border border-secondary-200 dark:border-secondary-600 disabled:opacity-30 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors">
                <ChevronLeft className="h-4 w-4 text-secondary-600 dark:text-secondary-400" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-secondary-200 dark:border-secondary-600 disabled:opacity-30 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors">
                <ChevronRight className="h-4 w-4 text-secondary-600 dark:text-secondary-400" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Campaign Detail Modal */}
      {selectedCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedCampaign(null)}>
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
              <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Campaign Details</h3>
              <button onClick={() => setSelectedCampaign(null)} className="p-1 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors">
                <X className="h-4 w-4 text-secondary-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <div className="text-lg font-semibold text-secondary-900 dark:text-white">{selectedCampaign.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <TemplatePill template={selectedCampaign.template} />
                  <StatusPill status={selectedCampaign.status} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-3">
                  <div className="text-xs text-secondary-500 dark:text-secondary-400">Subject</div>
                  <div className="text-sm font-medium text-secondary-900 dark:text-white mt-0.5">{selectedCampaign.subject}</div>
                </div>
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-3">
                  <div className="text-xs text-secondary-500 dark:text-secondary-400">Recipients</div>
                  <div className="text-sm font-medium text-secondary-900 dark:text-white mt-0.5">{selectedCampaign.total_recipients}</div>
                </div>
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-3">
                  <div className="text-xs text-secondary-500 dark:text-secondary-400">Successful</div>
                  <div className="text-sm font-medium text-green-600 dark:text-green-400 mt-0.5">{selectedCampaign.successful}</div>
                </div>
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-3">
                  <div className="text-xs text-secondary-500 dark:text-secondary-400">Failed</div>
                  <div className="text-sm font-medium text-red-500 dark:text-red-400 mt-0.5">{selectedCampaign.failed}</div>
                </div>
              </div>
              <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-3">
                <div className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Body</div>
                <div className="text-sm text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap">{selectedCampaign.body}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-secondary-500 dark:text-secondary-400">
                <div>Created: {formatDate(selectedCampaign.created_at)}</div>
                {selectedCampaign.scheduled_at && <div>Scheduled: {formatDate(selectedCampaign.scheduled_at)}</div>}
                {selectedCampaign.sent_at && <div>Sent: {formatDate(selectedCampaign.sent_at)}</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Analytics Tab ──────────────────────────────────────────────────────────

const AnalyticsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get('/api/promotional-emails/stats', { headers: API_HEADERS() });
        setStats(res.data);
      } catch { toast.error('Failed to load stats'); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Mail} label="Total Campaigns" value={stats.total_campaigns} tone="primary" />
        <StatCard icon={CheckCircle} label="Emails Delivered" value={stats.total_sent_emails} tone="green" />
        <StatCard icon={XCircle} label="Emails Failed" value={stats.total_failed_emails} tone="red" />
        <StatCard icon={Clock} label="Scheduled" value={stats.scheduled_upcoming} tone="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-6">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-4">Campaigns by Status</h3>
          {stats.by_status && Object.keys(stats.by_status).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(stats.by_status).map(([status, count]) => {
                const pct = stats.total_campaigns ? Math.round((count / stats.total_campaigns) * 100) : 0;
                const meta = STATUS_META[status] || STATUS_META.draft;
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1">
                      <StatusPill status={status} />
                      <span className="text-xs font-medium text-secondary-700 dark:text-secondary-300">{count}</span>
                    </div>
                    <div className="w-full bg-secondary-100 dark:bg-secondary-700 rounded-full h-2">
                      <div className="bg-primary-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">No data yet</p>
          )}
        </div>

        <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-6">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-4">Delivery Overview</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600 dark:text-secondary-400">Total Recipients</span>
              <span className="text-sm font-semibold text-secondary-900 dark:text-white">{stats.total_recipients}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600 dark:text-secondary-400">Successfully Delivered</span>
              <span className="text-sm font-semibold text-green-600 dark:text-green-400">{stats.total_sent_emails}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600 dark:text-secondary-400">Failed</span>
              <span className="text-sm font-semibold text-red-500 dark:text-red-400">{stats.total_failed_emails}</span>
            </div>
            <div className="pt-3 border-t border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">Success Rate</span>
                <span className="text-lg font-bold text-primary-600 dark:text-primary-400">{stats.success_rate}%</span>
              </div>
              <div className="w-full bg-secondary-100 dark:bg-secondary-700 rounded-full h-2.5 mt-2">
                <div className="bg-green-500 h-2.5 rounded-full transition-all" style={{ width: `${stats.success_rate}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const PromotionalEmails = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [refreshKey, setRefreshKey] = useState(0);

  const tabs = [
    { id: 'compose', label: 'Compose', Icon: Plus },
    { id: 'campaigns', label: 'Campaigns', Icon: Mail },
    { id: 'analytics', label: 'Analytics', Icon: BarChart3 },
  ];

  return (
    <div className="w-full min-w-0 max-w-none space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-secondary-900 dark:text-white">Promotional Emails</h1>
          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">Create and manage email campaigns for your team</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200 dark:border-secondary-700">
        <nav className="flex gap-6" aria-label="Tabs">
          {tabs.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:border-secondary-300'
                }`}>
                <tab.Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'compose' && (
        <ComposeTab onCampaignCreated={() => setRefreshKey(k => k + 1)} />
      )}
      {activeTab === 'campaigns' && <CampaignsTab refreshKey={refreshKey} />}
      {activeTab === 'analytics' && <AnalyticsTab />}
    </div>
  );
};

export default PromotionalEmails;
